import atinaryLogo from "../../../assets/atinary_logo_sm.png";
import AppMenu from "./AppMenu";
import ExperimentSwitcher from "./ExperimentSwitcher";
import HeaderActionIcon from "./HeaderActionIcon";
import HeaderButton from "./HeaderButton";
import HeaderTitle from "./HeaderTitle";
import styles from "./Page.module.css";
import PageFooter from "./PageFooter";
import SidebarLayout from "./SidebarLayout";
import TeamSwitcherButton from "./TeamSwitcherButton";
import { HEADER_HEIGHT } from "./constants";
import posthogEvents from "@app/config/posthog/posthog-events";
import type { SplitPaneProps } from "@gfazioli/mantine-split-pane";
import Icon from "@lib/common/components/Icon/Icon";
import type { BoxProps } from "@mantine/core";
import { AppShell, Box, Flex, Group, ScrollArea, Text, Tooltip } from "@mantine/core";
import { useOs } from "@mantine/hooks";
import { spotlight } from "@mantine/spotlight";
import { isNil } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { type ReactNode, useCallback } from "react";
import { NavLink } from "react-router";

export type PageProps = {
  children: ReactNode;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  /** Content of the resizable pane next to the main content. The pane shows only while this is set. */
  sidebar?: ReactNode;
  sidebarSplitPaneProps?: Partial<SplitPaneProps>;
  contentBoxProps?: BoxProps;
};

export default function Page({
  children,
  headerLeft,
  headerRight,
  sidebar,
  sidebarSplitPaneProps,
  contentBoxProps
}: PageProps) {
  const os = useOs();
  const posthog = usePostHog();

  const onHomeClick = useCallback(() => {
    posthog.capture(posthogEvents.page_header.home_button_click);
  }, [posthog]);

  const onSpotlightSearchClick = useCallback(() => {
    posthog.capture(posthogEvents.page_header.spotlight_search_button_click);
    spotlight.open();
  }, [posthog]);

  return (
    <AppShell mode="fixed" padding={0} header={{ height: HEADER_HEIGHT }}>
      <AppShell.Header pr="sm" className={styles.header}>
        <Group h="100%" align="center" gap={5} justify="space-between" wrap="nowrap">
          <Group h="100%" gap="sm" wrap="nowrap">
            <Group h="100%" gap={5} wrap="nowrap">
              <Tooltip label="SDLabs® by Atinary Technologies">
                <Flex ml="xs">
                  <img src={atinaryLogo} width={27} />
                </Flex>
              </Tooltip>
              <AppMenu />
              <Page.HeaderActionIcon component={NavLink} to="/" onClick={onHomeClick}>
                <Icon name="home" />
              </Page.HeaderActionIcon>
              <Tooltip label={`Search ${os === "macos" ? "Cmd+K" : "Ctrl+K"}`}>
                <Page.HeaderActionIcon onClick={onSpotlightSearchClick}>
                  <Icon name="search" />
                </Page.HeaderActionIcon>
              </Tooltip>
              <TeamSwitcherButton />
              {!isNil(headerLeft) && <Text opacity={0.3}>/</Text>}
            </Group>
            <Group gap={5} wrap="nowrap">
              {headerLeft}
            </Group>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {headerRight}
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <SidebarLayout showSidebar={!isNil(sidebar)} sidebar={sidebar} sidebarSplitPaneProps={sidebarSplitPaneProps}>
          <ScrollArea h={`calc(100vh - ${HEADER_HEIGHT}px)`}>
            <Box miw={600} w="100%" mx="auto" px="md" py="md" pos="relative" {...contentBoxProps}>
              <Box mih={`calc(100vh - ${HEADER_HEIGHT}px)`}>{children}</Box>
              <PageFooter />
            </Box>
          </ScrollArea>
        </SidebarLayout>
      </AppShell.Main>
    </AppShell>
  );
}

Page.HeaderTitle = HeaderTitle;
Page.HeaderButton = HeaderButton;
Page.HeaderActionIcon = HeaderActionIcon;
Page.ExperimentSwitcher = ExperimentSwitcher;

Page.HeaderDivider = function HeaderDivider() {
  return (
    <Text size="sm" opacity={0.3}>
      /
    </Text>
  );
};
